/**
 * Training.jsx: BM-H3630 (5 Oct 2026). Staff training in CaseMinder.
 *
 * Vanessa: "create training for the team ... add to each person's dashboard
 * and provide deadlines/reminders for annual AML training", then "run October
 * for AML training". Each person sees their courses and due dates, reads the
 * materials and takes the assessment; a pass is recorded on REG-08, the
 * Training and CPD Register. The MLCO, COLP and directors see everyone.
 * Server: /api/compliance/training.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle, ArrowLeft, Briefcase, Building2, Calculator, CheckCircle2, ChevronRight, Clock, Coins, FileSignature,
  FileText, FolderOpen, Gauge, GraduationCap, HeartHandshake, Home, Landmark, ListChecks, Mail, PoundSterling,
  RotateCcw, Scale, ShieldCheck, Sparkles, Target, TrendingDown, XCircle,
} from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { CM_ENABLED, cmApi } from "./mvp/cmApi";

function authHeaders() {
  const id = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return id ? { "X-Bonsai-User-Id": id } : {};
}

// BM-H4249: a passed quiz can go into the person's competence log, once per
// REG-08 entry. The server claims it by the REG-08 ref, so a second click or
// a second tab returns the first entry instead of adding another.
function LogCompetence({ course, result }) {
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const add = async () => {
    setState("busy");
    setError("");
    try {
      const done = await cmApi.addCompetenceActivity({
        date: new Date().toLocaleDateString("en-CA"), // yyyy-mm-dd, local day
        title: course.title,
        activity_type: "Course or webinar",
        assessed: true,
        assessment: `In-house quiz passed, ${result.score}% (pass mark ${result.pass_mark}%); REG-08 ${result.register_ref}`,
        source_ref: result.register_ref,
      });
      setState(done.already_logged ? "already" : "added");
    } catch (err) {
      setState("idle");
      setError(errorMessage(err, "It could not be added to your competence log"));
    }
  };
  if (state === "added" || state === "already") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-emerald-300" data-testid="training-competence-logged">
        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        {state === "added" ? "Added to your competence log." : "Already in your competence log."}
        <Link to="/compliance/mvp/competence" className="underline">Add what you will do differently</Link>
      </span>
    );
  }
  return (
    <>
      <button type="button" onClick={add} disabled={state === "busy"}
        className="rounded-md border border-[var(--brand-line)] px-3 py-1.5 text-sm text-[var(--brand-text)] hover:bg-[var(--brand-dim)] disabled:opacity-60"
        data-testid="training-add-competence">
        {state === "busy" ? "Adding…" : "Add this to your competence log"}
      </button>
      {error && <span className="text-sm text-red-300">{error}</span>}
    </>
  );
}

const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { dateStyle: "medium" }) : "");
export const STATUS = {
  current: ["Done for this year", "text-emerald-300"],
  due: ["Due", "text-slate-200"],
  due_soon: ["Due soon", "text-amber-300"],
  overdue: ["Overdue", "text-red-300"],
  to_do: ["To do", "text-slate-200"], // BM-H4218: one-off course, no due date
};

// BM-H4218: a one-off course has no due date, so it is "To do" or "Completed".
export function statusWords(c) {
  const [label] = STATUS[c.status] || [c.status];
  if (c.annual === false) return c.status === "current" ? `Completed ${day(c.last_completed)}` : label;
  return c.status === "current" ? `${label} (completed ${day(c.last_completed)}; next due ${day(c.due)})` : `${label} by ${day(c.due)}`;
}

function StatusLine({ c }) {
  const [, cls] = STATUS[c.status] || [c.status, ""];
  return (
    <span className={`text-sm ${cls}`} data-testid={`training-status-${c.course_id}`}>
      {statusWords(c)}
    </span>
  );
}

// BM-H4218: Vanessa, 12 Oct 2026: "can all quizzes including the AML quiz
// please be a bit jazzier, more visually appealing?" Brand indigo for actions
// (tokens in index.css); green, amber and red only for pass, fail and status.
const pct = (n, d) => (d ? Math.round((100 * n) / d) : 0);

function Pill({ children, className = "" }) {
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}>{children}</span>;
}

// 26 Oct 2026, Vanessa: "can you look at the quizzes and jazz up like the
// agents?" Each course type has its own hue and each category its own icon,
// as on the Bonsai Agents page. A type or category added later (the SoF and
// SoW quiz, say) falls back to indigo and the mortar board. Hues stay off
// green, amber and red, which mean pass, fail and status here.
const TYPE_HUES = {
  Compliance: ["#6366f1", "#8b5cf6"],
  "Legal knowledge": ["#ec4899", "#a855f7"],
  "System and processes": ["#06b6d4", "#3b82f6"],
  "Practice areas": ["#3b82f6", "#a855f7"],
};
const FALLBACK_HUE = ["#6366f1", "#a855f7"];
const CATEGORY_ICONS = [
  [/source of (funds|wealth)|\bsof\b|\bsow\b/i, Coins],
  [/aml|money laundering/i, ShieldCheck],
  [/payment/i, PoundSterling],
  [/risk/i, Gauge],
  [/company/i, Building2],
  [/insolvency/i, TrendingDown],
  [/dispute|litigation/i, Scale],
  [/file opening/i, FolderOpen],
  [/time/i, Clock],
  [/client care|ccl/i, FileSignature],
  [/account/i, Calculator],
  [/mail/i, Mail],
  [/private client|wills|probate/i, HeartHandshake],
  [/residential|conveyanc/i, Home],
  [/commercial/i, Briefcase],
  [/property|planning/i, Landmark],
  [/fraud|scam/i, AlertTriangle],
];
const hueFor = (type) => TYPE_HUES[type] || FALLBACK_HUE;
const hueStyle = (type) => { const [c1, c2] = hueFor(type); return { "--c1": c1, "--c2": c2 }; };
const iconFor = (category) => (CATEGORY_ICONS.find(([re]) => re.test(category || "")) || [null, GraduationCap])[1];

function TypeFilter({ types, courses, value, onChange }) {
  // Shown only once the server says which types exist; an older server sends
  // none, and then every course is listed as before.
  if (types.length < 2) return null;
  const chip = (label, t, n) => {
    const on = value === t;
    return (
      <button key={label} type="button" aria-pressed={on} onClick={() => onChange(t)}
        data-testid={`training-filter-${t ? t.toLowerCase().replace(/ /g, "-") : "all"}`}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm ring-1 transition ${on
          ? "cm-glass-strong text-white ring-white/40"
          : "cm-glass cm-glass-hover text-indigo-100/85 ring-white/15 hover:text-white"}`}>
        {t && <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: `linear-gradient(135deg, ${hueFor(t).join(", ")})` }} />}
        {label}<span className={`text-xs ${on ? "text-white/80" : "text-indigo-200/60"}`}>{n}</span>
      </button>
    );
  };
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Filter courses by type" data-testid="training-filter">
      {chip("All", "", courses.length)}
      {types.map((t) => chip(t, t, courses.filter((c) => c.type === t).length))}
    </div>
  );
}

function ProgressBar({ value, className = "" }) {
  return (
    <div className={`h-2 w-full overflow-hidden rounded-full bg-slate-800 ${className}`}>
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${value}%`, background: "linear-gradient(90deg, var(--c1, var(--brand)), var(--c2, var(--brand)))" }} />
    </div>
  );
}

function ScoreRing({ score, passed }) {
  const colour = passed ? "#34d399" : "#fbbf24";
  return (
    <div className="relative h-28 w-28 shrink-0 rounded-full"
      style={{ background: `conic-gradient(${colour} ${score * 3.6}deg, rgba(51,65,85,0.6) 0deg)` }}>
      <div className="absolute inset-2 flex flex-col items-center justify-center rounded-full bg-slate-950">
        <span className="text-3xl font-bold text-slate-50">{score}%</span>
        <span className="text-[11px] uppercase tracking-wide text-slate-400">score</span>
      </div>
    </div>
  );
}

function MyRecord({ data }) {
  const rows = data.record || [];
  return (
    <section id="record" className="space-y-2" data-testid="training-record">
      <h2 className="text-base font-semibold text-slate-100">Your training record</h2>
      <p className="text-xs text-slate-400">From the firm's Training and CPD Register. A passed quiz is added automatically.</p>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-400">Nothing recorded yet.</div>
      ) : (
        <ul className="divide-y divide-slate-800/70 rounded-lg border border-slate-700/60">
          {rows.map((r) => (
            <li key={r.reference} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid="training-record-row">
              <span className="flex items-center gap-2 text-sm text-slate-100">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                {r.training}
              </span>
              <span className="text-xs text-slate-400">{[day(r.date), r.outcome].filter(Boolean).join(", ")}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function MyTraining() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.get(`${API}/compliance/training/my`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch((err) => setError(errorMessage(err, "Your training could not be loaded")));
  }, []);
  // BM-H4449: filter the list by course type, held in the address so a link
  // can open the page already filtered (for example ?type=Compliance).
  const [params, setParams] = useSearchParams();
  const types = (data?.types || []).filter((t) => (data?.courses || []).some((c) => c.type === t));
  const type = types.includes(params.get("type")) ? params.get("type") : "";
  const setType = (t) => setParams(t ? { type: t } : {}, { replace: true });
  const shown = (data?.courses || []).filter((c) => !type || c.type === type);
  const all = data?.courses || [];
  const done = all.filter((c) => c.status === "current").length;
  const overdue = all.filter((c) => c.status === "overdue").length;
  const todo = all.length - done - overdue;
  const { hash } = useLocation();
  useEffect(() => {
    if (data && hash === "#record") document.getElementById("record")?.scrollIntoView?.({ behavior: "smooth" });
  }, [data, hash]);
  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="training-my">
      <div className="cm-hero relative overflow-hidden rounded-2xl border border-white/10 p-5 shadow-xl shadow-indigo-950/40">
        <div aria-hidden className="agents-orb absolute -top-24 -right-16 h-72 w-72 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(236,72,153,0.30), transparent 65%)" }} />
        <div className="relative flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ring-white/20 shadow-lg shadow-fuchsia-900/40"
              style={{ background: "linear-gradient(135deg, #6366f1, #a855f7 55%, #ec4899)" }}>
              <GraduationCap className="h-6 w-6 text-white" aria-hidden="true" />
              <Sparkles size={14} className="agents-twinkle absolute -top-1 -right-1 text-amber-200" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-2xl font-bold agents-title leading-tight">Training</h1>
              <p className="text-sm text-indigo-100/75">Read the note, take the quiz, and a pass goes on your record.</p>
            </div>
          </div>
          <Link to="/training-overview" className="cm-glass cm-glass-hover rounded-full px-3 py-1.5 text-sm text-white ring-1 ring-white/20">Firm overview</Link>
        </div>
        {data && (
          <div className="relative mt-4 flex flex-wrap gap-2 text-sm" data-testid="training-tally">
            <span className="cm-glass inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-emerald-300 ring-1 ring-white/10">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> {done} done</span>
            <span className="cm-glass inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-indigo-100 ring-1 ring-white/10">
              <ListChecks className="h-4 w-4" aria-hidden="true" /> {todo} to do</span>
            {overdue > 0 && <span className="cm-glass inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-red-300 ring-1 ring-white/10">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" /> {overdue} overdue</span>}
          </div>
        )}
        {data && <div className="relative mt-4"><TypeFilter types={types} courses={data.courses} value={type} onChange={setType} /></div>}
      </div>
      {error && <div className="text-sm text-red-300">{error}</div>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((c) => {
          const Icon = iconFor(c.category);
          return (
            <Link key={c.course_id} to={`/training/${c.course_id}`} state={{ type: c.type, category: c.category }}
              data-testid={`training-course-${c.course_id}`} style={hueStyle(c.type)}
              className="cm-card group relative flex flex-col gap-2 overflow-hidden rounded-xl border border-white/[0.07] bg-navy-card p-4">
              <div aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: "linear-gradient(90deg, var(--c1), var(--c2))" }} />
              <div aria-hidden className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-25"
                style={{ background: "radial-gradient(circle, var(--c1), transparent 70%)" }} />
              <div className="relative flex items-center gap-2.5">
                <span className="cm-tile flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="h-5 w-5 text-white" aria-hidden="true" />
                </span>
                <span className="cm-label text-xs font-semibold uppercase tracking-wide">{c.category}</span>
                {c.status === "current" && <CheckCircle2 className="ml-auto h-5 w-5 text-emerald-400" aria-label="Done" />}
              </div>
              <div className="relative text-sm font-semibold text-slate-100">{c.title}</div>
              <div className="relative mt-auto flex items-center justify-between gap-2">
                <StatusLine c={c} />
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-white" aria-hidden="true" />
              </div>
            </Link>
          );
        })}
      </div>
      {data && <MyRecord data={data} />}
    </div>
  );
}

function Course({ courseId }) {
  // The list passes the course's type and category along, for its hue and icon.
  const { state: from } = useLocation();
  const [course, setCourse] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // BM-H4218: a course's own document needs the sign-in header, so it is
  // fetched here and opened from memory rather than linked directly.
  const [fileError, setFileError] = useState("");
  const openFile = async (file) => {
    setFileError("");
    const tab = window.open("", "_blank"); // opened on the click, so no pop-up blocker
    try {
      const r = await axios.get(`${API}/compliance/training/courses/${courseId}/materials/${file}`,
        { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      if (tab) tab.close();
      setFileError(errorMessage(err, "The document could not be opened"));
    }
  };
  const load = useCallback(() => {
    axios.get(`${API}/compliance/training/courses/${courseId}`, { headers: authHeaders() })
      .then((r) => { setCourse(r.data); setAnswers({}); setResult(null); })
      .catch((err) => setError(errorMessage(err, "The course could not be loaded")));
  }, [courseId]);
  useEffect(() => { load(); }, [load]);
  if (error && !course) return <div className="p-6 text-sm text-red-300">{error}</div>;
  if (!course) return <div className="p-6 text-sm text-slate-400">Loading…</div>;
  const total = course.questions.length;
  const answered = Object.keys(answers).length;
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/compliance/training/courses/${courseId}/attempts`, { answers }, { headers: authHeaders() });
      setResult(r.data);
      // The page scrolls inside the app shell, not the window, so bring the
      // result itself into view.
      setTimeout(() => document.querySelector("[data-testid='training-result']")?.scrollIntoView?.({ behavior: "smooth", block: "start" }), 0);
    } catch (err) {
      setError(errorMessage(err, "Your answers could not be submitted"));
    } finally {
      setBusy(false);
    }
  };
  const right = result ? result.results.filter((r) => r.correct).length : 0;
  const hasMaterials = (course.materials || []).length > 0; // BM-H4226: quizzes with no reading note
  const type = course.type || from?.type;
  const category = course.category || from?.category;
  const Icon = iconFor(category);
  const Step = ({ n }) => <span className="cm-tile flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white">{n}</span>;
  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl" data-testid="training-course" style={hueStyle(type)}>
      <Link to="/training" className="inline-flex items-center gap-1 text-sm text-[var(--brand-text)]">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> My training
      </Link>

      <header className="cm-hero relative overflow-hidden rounded-2xl border border-white/10 p-5 shadow-xl shadow-indigo-950/40 sm:p-6">
        <div aria-hidden className="agents-orb absolute -right-16 -top-20 h-64 w-64 rounded-full opacity-60"
          style={{ background: "radial-gradient(circle, var(--c2), transparent 65%)" }} />
        <div className="relative flex items-start gap-4">
          <div className="cm-tile relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-1 ring-white/20">
            <Icon className="h-7 w-7 text-white" aria-hidden="true" />
            <Sparkles size={14} className="agents-twinkle absolute -top-1 -right-1 text-amber-200" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <Pill className="cm-glass text-indigo-100 ring-1 ring-white/15"><GraduationCap className="h-3.5 w-3.5" aria-hidden="true" /> {category || "Training"}</Pill>
            <h1 className="mt-1.5 text-2xl font-bold agents-title">{course.title}</h1>
            <p className="mt-2 text-sm text-indigo-100/80">{course.summary}</p>
          </div>
        </div>
        <div className="relative mt-4 flex flex-wrap gap-2">
          <Pill className="cm-glass text-white ring-1 ring-white/15"><ListChecks className="h-3.5 w-3.5" aria-hidden="true" /> {total} questions</Pill>
          <Pill className="cm-glass text-white ring-1 ring-white/15"><Target className="h-3.5 w-3.5" aria-hidden="true" /> Pass mark {course.pass_mark}%</Pill>
          <Pill className="cm-glass text-white ring-1 ring-white/15"><RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Retake as often as you like</Pill>
        </div>
      </header>

      {result && (
        <section className={`rounded-2xl border p-5 ${result.passed ? "border-emerald-600/60 bg-emerald-950/30" : "border-amber-600/60 bg-amber-950/20"}`} data-testid="training-result">
          <div className="flex flex-wrap items-center gap-5">
            <ScoreRing score={result.score} passed={result.passed} />
            <div className="space-y-1">
              <div className={`text-xl font-bold ${result.passed ? "text-emerald-300" : "text-amber-300"}`}>
                {result.passed ? `🎉 Passed: ${result.score}%` : `Not passed: ${result.score}% (the pass mark is ${result.pass_mark}%)`}
              </div>
              <div className="text-sm text-slate-300">{right} of {total} correct.</div>
              {result.passed ? (
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <span className="inline-flex items-center gap-1.5 text-sm text-emerald-300" data-testid="training-recorded">
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Added to your training record ({result.register_ref})
                  </span>
                  <Link to="/training#record" className="rounded-md border border-[var(--brand-line)] px-3 py-1.5 text-sm text-[var(--brand-text)] hover:bg-[var(--brand-dim)]"
                    data-testid="training-see-record">See your training record</Link>
                  {CM_ENABLED && result.register_ref && <LogCompetence course={course} result={result} />}
                </div>
              ) : (
                <div className="text-sm text-slate-300">Read the explanations below, then try again.</div>
              )}
            </div>
          </div>
          <ol className="mt-5 space-y-2">
            {result.results.map((r, i) => (
              <li key={r.id} className={`rounded-lg border-l-4 bg-slate-900/70 p-3 text-sm ${r.correct ? "border-emerald-500" : "border-red-500"}`}>
                <div className="flex items-start gap-2">
                  {r.correct ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                    : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden="true" />}
                  <div className="space-y-1">
                    <div className="text-slate-100"><span className="sr-only">{r.correct ? "Correct" : "Not correct"}: </span>{i + 1}. {r.question}</div>
                    {!r.correct && <div className="text-slate-300">The answer: <span className="font-medium text-slate-100">{r.answer}</span></div>}
                    <div className="text-slate-400">{r.explanation} <span className="italic">({r.ref})</span></div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          {!result.passed && (
            <button type="button" onClick={load} className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[var(--brand)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--brand-hover)]"
              data-testid="training-retake"><RotateCcw className="h-4 w-4" aria-hidden="true" /> Try again</button>
          )}
        </section>
      )}

      {hasMaterials && <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-100">
          <Step n={1} />
          Read the materials
        </h2>
        {fileError && <div className="text-sm text-red-300">{fileError}</div>}
        <ul className="grid gap-2 sm:grid-cols-2">
          {course.materials.map((m) => {
            const inner = (
              <>
                <span className="cm-tile flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"><FileText className="h-5 w-5 text-white" aria-hidden="true" /></span>
                <span className="text-sm text-slate-100">{m.title}</span>
              </>
            );
            const cls = "cm-card flex w-full items-center gap-3 rounded-xl border border-white/[0.07] bg-navy-card p-3 text-left";
            return (
              <li key={m.url || m.file}>
                {m.file ? <button type="button" onClick={() => openFile(m.file)} className={cls} data-testid={`training-material-${m.file}`}>{inner}</button>
                  : m.internal ? <Link to={m.url} className={cls}>{inner}</Link>
                    : <a href={m.url} target="_blank" rel="noreferrer" className={cls}>{inner}</a>}
              </li>
            );
          })}
        </ul>
      </section>}

      {!result && (
        <section className="space-y-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-100">
            <Step n={hasMaterials ? 2 : 1} />
            Take the quiz
          </h2>
          <div className="sticky top-0 z-10 -mx-1 rounded-lg bg-slate-950/90 px-1 py-2 backdrop-blur" data-testid="training-progress">
            <div className="mb-1 flex justify-between text-xs text-slate-400">
              <span>{answered} of {total} answered</span><span>{pct(answered, total)}%</span>
            </div>
            <ProgressBar value={pct(answered, total)} />
          </div>
          {course.questions.map((q, i) => (
            <fieldset key={q.id} className="rounded-xl border bg-navy-card p-4 transition"
              style={{ borderColor: answers[q.id] !== undefined ? "color-mix(in srgb, var(--c1) 55%, transparent)" : "rgba(255,255,255,0.07)" }}
              data-testid={`training-q-${q.id}`}>
              <legend className="sr-only">{i + 1}. {q.question}</legend>
              <div className="mb-3 flex items-start gap-3" aria-hidden="true">
                <span className="cm-tile flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white">{i + 1}</span>
                <span className="pt-0.5 text-sm font-medium text-slate-100">{q.question}</span>
              </div>
              <div className="grid gap-2">
                {q.options.map((o, j) => {
                  const on = answers[q.id] === o.id;
                  return (
                    // 29 Oct 2026: relative, so the hidden radio (sr-only is
                    // position: absolute) sits in its label and scrolls with
                    // the page. Without it the app shell held the radio far
                    // below the screen, and picking an answer scrolled the whole
                    // shell out of view: the quiz "went black".
                    <label key={o.id} htmlFor={`${q.id}-${o.id}`}
                      className={`relative flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 text-sm transition ${on ? "text-slate-50" : "border-slate-700/60 text-slate-200 hover:border-slate-500 hover:bg-slate-800/50"}`}
                      style={on ? { borderColor: "var(--c1)", background: "color-mix(in srgb, var(--c1) 18%, transparent)" } : undefined}>
                      <input type="radio" className="sr-only" name={q.id} id={`${q.id}-${o.id}`} checked={on}
                        onChange={() => setAnswers({ ...answers, [q.id]: o.id })} />
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${on ? "cm-tile text-white" : "bg-slate-800 text-slate-300"}`}>
                        {"ABCDEFGH"[j]}
                      </span>
                      <span className="pt-0.5">{o.text}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ))}
          {error && <div className="text-sm text-red-300">{error}</div>}
          <button type="button" disabled={busy || answered < total} onClick={submit}
            data-testid="training-submit" style={{ background: "linear-gradient(90deg, var(--c1), var(--c2))" }}
            className="agent-start inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 transition disabled:opacity-50 sm:w-auto">
            <Sparkles className="h-4 w-4" aria-hidden="true" /> Submit answers ({answered} of {total})
          </button>
        </section>
      )}
    </div>
  );
}

function EmailTheTeam({ courseId }) {
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const call = async (body) => {
    if (busy) return null; // BM-H3654: one click, one send
    setError("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/compliance/training/courses/${courseId}/announce`, body, { headers: authHeaders() });
      return r.data;
    } catch (err) {
      setError(errorMessage(err, "The email could not be sent"));
      return null;
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return <div className="text-sm text-emerald-300" data-testid="training-announce-done">Emailed {done.sent} of {done.count} people.{done.sent < done.count ? " Some did not send: see the overview again later." : ""}</div>;
  }
  return (
    <div className="space-y-1">
      {!preview ? (
        <button type="button" data-testid="training-announce" onClick={async () => setPreview(await call({}))}
          className="px-3 py-1.5 text-sm rounded-md border border-slate-600 text-slate-200">Email the team the training link</button>
      ) : (
        <div className="p-3 rounded-md border border-slate-700/60 text-sm text-slate-200 space-y-2" data-testid="training-announce-confirm">
          <div>This emails {preview.count} people: {preview.would_send_to.join(", ")}.</div>
          <button type="button" data-testid="training-announce-send" disabled={busy} onClick={async () => setDone(await call({ send: true }))}
            className="px-3 py-1.5 text-sm rounded-md bg-emerald-700 text-white disabled:opacity-50">{busy ? "Sending…" : "Send now"}</button>
          <button type="button" onClick={() => setPreview(null)} className="ml-2 px-3 py-1.5 text-sm rounded-md border border-slate-600">Cancel</button>
        </div>
      )}
      {error && <div className="text-sm text-red-300">{error}</div>}
    </div>
  );
}

export function TrainingOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.get(`${API}/compliance/training/overview`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch((err) => setError(errorMessage(err, "The overview could not be loaded")));
  }, []);
  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-5xl" data-testid="training-overview">
      <Link to="/training" className="text-sm text-sky-300">← My training</Link>
      <h1 className="text-xl font-semibold text-slate-100">Training: firm overview</h1>
      {error && <div className="text-sm text-red-300">{error}</div>}
      {data && data.courses.map((c) => (
        <section key={c.course_id} className="space-y-2">
          <h2 className="text-base font-semibold text-slate-100">{c.title}</h2>
          <EmailTheTeam courseId={c.course_id} />
          <div className="text-sm text-slate-300" data-testid={`training-counts-${c.course_id}`}>
            {c.in_scope} in scope: {c.counts.current} done, {c.counts.due + c.counts.due_soon + (c.counts.to_do || 0)} to do, {c.counts.overdue} overdue
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-slate-200">
              <thead><tr className="text-left text-xs text-slate-400"><th className="py-1 pr-3">Name</th><th className="py-1 pr-3">Status</th><th className="py-1 pr-3">Due</th><th className="py-1">Last completed</th></tr></thead>
              <tbody>
                {c.people.map((p) => {
                  const [label, cls] = STATUS[p.status] || [p.status, ""];
                  return (
                    <tr key={p.email} className="border-t border-slate-800/60" data-testid="training-overview-row">
                      <td className="py-1 pr-3">{p.name}</td>
                      <td className={`py-1 pr-3 ${cls}`}>{label}</td>
                      <td className="py-1 pr-3">{p.due ? day(p.due) : "No due date"}</td>
                      <td className="py-1">{day(p.last_completed) || (c.annual === false ? "Not yet" : "Not this year")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

export default function Training() {
  const { courseId } = useParams();
  return courseId ? <Course courseId={courseId} /> : <MyTraining />;
}
